import { lazy, Suspense, useEffect } from 'react';
import { Spin } from 'antd';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { ErrorBoundary } from './ErrorBoundary';
import { AuthGuard } from '../modules/auth/AuthGuard';
import { useAuthActions } from '../modules/auth/useAuth';

// 路由级懒加载（[CODING_STANDARDS §6 性能要点]）；ADR-0009 会在 Phase 4 进一步规整。
const LoginPage = lazy(() => import('../pages/login/LoginPage'));
const CaseListPage = lazy(() => import('../pages/cases/CaseListPage'));
const CaseDetailPage = lazy(() => import('../pages/cases/CaseDetailPage'));
// Demo viewer 是临时入口，未来要挪走（详见页面顶部注释）。
const DentalDemoViewerPage = lazy(() => import('../pages/demo/DentalDemoViewerPage'));
// DICOM viewer：独立全屏页（case detail 用 window.open 跳过来）。
// Cornerstone3D bundle ~1.5MB —— 必须路由级 lazy（[ADR-0009] 路由级代码分包）。
const DicomViewerPage = lazy(() => import('../pages/dicom-viewer/DicomViewerPage'));
// V1.5.2 [ADR-0022]：OrthoPlan UI scaffold —— Three.js + 28 项 nav + 4 panels；
// 路由级 lazy 加载（跟 DICOM viewer 同款，bundle 重）。
const OrthoPlanPage = lazy(() => import('../pages/ortho-plan/OrthoPlanPage'));
// RFC-0003 / [ADR-0027]：建模流程（分牙 → 牙冠 → 牙根 → 牙龈），沉浸式工具页，同样路由级 lazy。
const ModelSetupPage = lazy(() => import('../pages/model-setup/ModelSetupPage'));

function PageFallback() {
  return (
    <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
      <Spin />
    </div>
  );
}

export function App() {
  const { bootstrap } = useAuthActions();

  useEffect(() => {
    void bootstrap();
    // 仅启动一次 — bootstrap 来自 useCallback 在 hook 内稳定
  }, [bootstrap]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/cases"
            element={
              <AuthGuard>
                <AppLayout>
                  <CaseListPage />
                </AppLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/cases/:id"
            element={
              <AuthGuard>
                <AppLayout>
                  <CaseDetailPage />
                </AppLayout>
              </AuthGuard>
            }
          />
          {/* 临时演示路由 —— 未来 git mv 到其它位置即可，与业务无耦合 */}
          <Route
            path="/demo/dental-viewer"
            element={
              <AuthGuard>
                <AppLayout>
                  <DentalDemoViewerPage />
                </AppLayout>
              </AuthGuard>
            }
          />
          {/* DICOM viewer：独立全屏路由 —— 不包 AppLayout（沉浸式工具页，
              顶部 nav 不占像素更合理；从 case detail 用 window.open 跳过来）。
              URL 参数：?caseId=<id>&fileId=<id>（都可选） */}
          <Route
            path="/dicom-viewer"
            element={
              <AuthGuard>
                <DicomViewerPage />
              </AuthGuard>
            }
          />
          {/* V1.5.2 [ADR-0022]：OrthoPlan UI scaffold 路由 —— 不包 AppLayout
              （沉浸式工具页同 DICOM viewer）。
              URL 参数：caseId / planId（必须）。 */}
          <Route
            path="/cases/:caseId/plans/:planId"
            element={
              <AuthGuard>
                <OrthoPlanPage />
              </AuthGuard>
            }
          />
          <Route
            path="/cases/:caseId/model-setup"
            element={
              <AuthGuard>
                <ModelSetupPage />
              </AuthGuard>
            }
          />
          <Route path="/" element={<Navigate to="/cases" replace />} />
          <Route path="*" element={<Navigate to="/cases" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
